import { useForm } from "react-hook-form";
import { IEbayAspect, IPartAspect } from "../../models/EbayOffer";
import useEbayAspects from "../../data/useEbayAspects";
import usePartAspectsMutations from "../../data/usePartAspectsMutations";
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";
import ComboBoxInput from "../shared/Inputs/ComboBoxInput";
import ComboBoxMultipleInput from "../shared/Inputs/ComboBoxMultipleInput";

interface JobPartAspectsProps {
  partDataId: string;
  ebayCategoryId: string;
  dismantlerAspectNames: string[];
  existingAspects: IPartAspect[];
  onSaved?: () => void;
  /** Denser layout for job parts table rows (smaller labels, no section title). */
  compact?: boolean;
  /** Tile/card layout: inset panel with tighter typography. */
  embedded?: boolean;
}

export default function JobPartAspects({
  partDataId,
  ebayCategoryId,
  dismantlerAspectNames,
  existingAspects,
  onSaved,
  compact = false,
  embedded = false,
}: JobPartAspectsProps) {
  const { saveHandlers: aspectSaveHandlers } = useDefaultCRUDHandlers("Aspect");
  const { createUpdate: createUpdateAspects } = usePartAspectsMutations();
  const { aspects: ebayAspects } = useEbayAspects(ebayCategoryId);
  const { register } = useForm();

  const dismantlerAspects = (ebayAspects.data ?? []).filter((aspect) =>
    dismantlerAspectNames.includes(aspect.localizedAspectName)
  );

  if (!ebayCategoryId || dismantlerAspects.length === 0) return null;

  const getAspectValue = (aspectName: string) => {
    return existingAspects.find((aspect) => aspect.name === aspectName)?.value ?? "";
  };

  const saveAspectValue = (aspect: IEbayAspect, value: string) => {
    const existingAspect = existingAspects.find(
      (partAspect) => partAspect.name === aspect.localizedAspectName
    );
    const existingValue = existingAspect?.value ?? "";

    if (existingValue === value) return;
    if (!value && !existingValue) return;

    const body = {
      partDataId,
      aspects: [
        existingAspect
          ? { ...existingAspect, value }
          : {
            name: aspect.localizedAspectName,
            value,
            isCustom: false,
          }
      ]
    };

    createUpdateAspects.mutate(body, {
      ...aspectSaveHandlers,
      onSuccess: () => {
        onSaved?.();
        aspectSaveHandlers.onSuccess();
      },
      onError: () => {
        aspectSaveHandlers.onError();
      }
    });
  };

  return (
    <div
      className={
        compact
          ? "max-h-40 space-y-1 overflow-y-auto rounded-md border border-slate-200/90 bg-slate-50/50 p-2"
          : embedded
            ? "space-y-2.5 rounded-lg border border-slate-200/90 bg-white p-3 shadow-sm ring-1 ring-slate-100/80"
            : "space-y-2 rounded border border-gray-200 p-3"
      }
    >
      {!compact && (
        <p
          className={
            embedded
              ? "text-[11px] font-bold uppercase tracking-wide text-slate-500"
              : "text-sm font-semibold text-gray-700"
          }
        >
          Part specifics
        </p>
      )}
      <div
        className={
          compact
            ? "flex flex-col gap-1.5"
            : embedded
              ? "flex flex-col gap-2.5 sm:flex-row sm:flex-wrap"
              : "flex flex-wrap gap-x-4 gap-y-2"
        }
      >
        {dismantlerAspects.map((aspect) => {
          const aspectValue = getAspectValue(aspect.localizedAspectName);
          const hasOptions = aspect.aspectValues && aspect.aspectValues.length > 0;
          const isMulti = aspect.aspectConstraint.itemToAspectCardinality === "MULTI";
          const label = aspect.aspectConstraint.aspectRequired
            ? `${aspect.localizedAspectName} *`
            : aspect.localizedAspectName;
          const multiSelectedValues = aspectValue
            ? aspectValue.split(", ").map((value) => value.trim()).filter((value) => value.length > 0)
            : [];

          return (
            <div
              key={aspect.localizedAspectName}
              className={
                compact
                  ? "flex flex-col gap-0.5"
                  : embedded
                    ? "flex min-w-0 flex-1 flex-col gap-1 sm:min-w-[10rem] sm:max-w-[14rem]"
                    : "flex flex-col gap-1"
              }
            >
              <label
                className={
                  compact
                    ? "line-clamp-2 text-[10px] font-medium uppercase tracking-wide text-slate-500"
                    : embedded
                      ? "line-clamp-2 text-xs font-medium text-slate-600"
                      : "text-sm font-medium text-gray-500"
                }
              >
                {label}
              </label>
              {hasOptions && isMulti ? (
                <ComboBoxMultipleInput
                  key={`${aspect.localizedAspectName}-${aspectValue}`}
                  size="sm"
                  options={aspect.aspectValues}
                  searchField={"localizedValue"}
                  register={register}
                  setValue={(_registerName: string, value: string) => {
                    saveAspectValue(aspect, value ?? "");
                  }}
                  registerName={aspect.localizedAspectName}
                  initialValue={multiSelectedValues.join(", ")}
                />
              ) : hasOptions ? (
                <ComboBoxInput
                  key={`${aspect.localizedAspectName}-${aspectValue}`}
                  size="sm"
                  options={aspect.aspectValues}
                  searchField={"localizedValue"}
                  register={register}
                  setValue={(_registerName: string, value: any) => {
                    saveAspectValue(aspect, value?.localizedValue ?? "");
                  }}
                  registerName={aspect.localizedAspectName}
                  initialValue={aspectValue}
                  placeholder="Select an option"
                  classes={
                    compact
                      ? "min-w-0 w-full max-w-full"
                      : embedded
                        ? "min-w-0 w-full"
                        : "min-w-52"
                  }
                />
              ) : (
                <input
                  type="text"
                  className={
                    compact
                      ? "w-full rounded border border-gray-300 px-1.5 py-0.5 text-xs"
                      : "rounded border border-gray-300 px-2 py-1 text-sm"
                  }
                  defaultValue={aspectValue}
                  maxLength={50}
                  placeholder="Enter value"
                  onBlur={(e) => saveAspectValue(aspect, e.target.value)}
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
